/**
 * QuestHub - Main kid-facing dashboard
 * 
 * Gaming vernacular without Roblox IP:
 * - Quests (not challenges)
 * - XP (experience points)
 * - Loot (rewards)
 * - Respawn (retry)
 * - Level Up (progression)
 * - Inventory (badges/achievements)
 * 
 * NEW: Scenario + Lens system for progressive learning
 */

import { useState, useEffect } from 'react';
import { useVoice } from '../hooks/useVoice';
import { tracker } from '../lib/tracking';
import { 
  getRecommendations, 
  getHint,
  type ChallengeRecommendation,
  type StudentProfile,
  type HintResponse,
} from '../lib/adaptive';
import {
  LEVEL_INFO,
  LEVEL_ORDER,
  type MathLevel,
  type QuestionLens,
} from '../lib/scenarios';
import { ScenarioQuestHub } from '../components/ScenarioQuest';
import { ScenarioSelector } from '../components/LearningArc';

// Types
interface Quest {
  id: string;
  title: string;
  skillTag: string;
  difficulty: 'easy' | 'medium' | 'hard';
  xpReward: number;
  status: 'locked' | 'available' | 'active' | 'completed';
  progress?: number;
}

interface PlayerStats {
  level: number;
  currentXP: number;
  xpToNextLevel: number;
  totalXP: number;
  streak: number;
  coins: number;
}

// XP bar component
function XPBar({ current, max, level }: { current: number; max: number; level: number }) {
  const percentage = Math.min((current / max) * 100, 100);
  
  return (
    <div className="relative">
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-blox-surface border-2 border-blox-accent shadow-lg shadow-blox-accent/20">
          <span className="font-display text-lg text-blox-accent">{level}</span>
        </div>
        <div className="flex-1">
          <div className="h-4 bg-blox-dark rounded-full overflow-hidden border border-blox-surface">
            <div 
              className="h-full bg-gradient-to-r from-blox-xp to-blox-accent rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${percentage}%` }}
            />
          </div>
          <div className="flex justify-between mt-1 text-xs text-slate-400 font-mono">
            <span>{current} XP</span>
            <span>{max} XP</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Streak flame component
function StreakFlame({ days }: { days: number }) {
  if (days === 0) return null;
  
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30">
      <span className="text-2xl animate-pulse">🔥</span>
      <div>
        <div className="text-sm font-semibold text-orange-300">{days} Day Streak</div>
        <div className="text-xs text-orange-400/70">Keep it going!</div>
      </div>
    </div>
  );
}

// Quest card component
function QuestCard({ 
  quest, 
  recommended,
  onStart,
  onContinue,
}: { 
  quest: Quest;
  recommended?: ChallengeRecommendation;
  onStart: () => void;
  onContinue?: () => void;
}) {
  const difficultyColors = {
    easy: 'text-blox-xp border-blox-xp/30 bg-blox-xp/10',
    medium: 'text-blox-gold border-blox-gold/30 bg-blox-gold/10',
    hard: 'text-blox-epic border-blox-epic/30 bg-blox-epic/10',
  };

  const statusStyles = {
    locked: 'opacity-50 grayscale',
    available: '',
    active: 'ring-2 ring-blox-accent',
    completed: 'opacity-70',
  };

  return (
    <div className={`relative group rounded-2xl bg-blox-card border border-blox-surface p-4 transition-all hover:border-blox-accent/50 ${statusStyles[quest.status]}`}>
      {/* Difficulty badge */}
      <div className={`absolute -top-2 -right-2 px-2 py-0.5 rounded-lg text-xs font-semibold uppercase tracking-wider border ${difficultyColors[quest.difficulty]}`}>
        {quest.difficulty}
      </div>

      {/* Quest info */}
      <div className="mb-3">
        <h3 className="font-display text-lg text-white">{quest.title}</h3>
        <p className="text-sm text-slate-400 mt-1">{quest.skillTag.replace('.', ' › ')}</p>
      </div>

      {/* AI recommendation reason */}
      {recommended && (
        <div className="mb-3 p-2 rounded-lg bg-blox-accent/10 border border-blox-accent/20">
          <p className="text-xs text-blox-accent">{recommended.reason}</p>
        </div>
      )}

      {/* XP reward */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-blox-xp font-semibold">+{quest.xpReward} XP</span>
        </div>

        {/* Action button */}
        {quest.status === 'available' && (
          <button
            onClick={onStart}
            className="px-4 py-2 rounded-xl bg-blox-accent text-blox-dark font-semibold text-sm hover:bg-blox-accent/90 transition-colors"
          >
            Start Quest
          </button>
        )}
        {quest.status === 'active' && onContinue && (
          <button
            onClick={onContinue}
            className="px-4 py-2 rounded-xl bg-blox-gold text-blox-dark font-semibold text-sm hover:bg-blox-gold/90 transition-colors"
          >
            Continue
          </button>
        )}
        {quest.status === 'completed' && (
          <span className="px-3 py-1 rounded-lg bg-blox-success/20 text-blox-success text-sm">
            ✓ Cleared
          </span>
        )}
        {quest.status === 'locked' && (
          <span className="text-slate-500 text-sm">🔒 Locked</span>
        )}
      </div>

      {/* Progress bar for active quests */}
      {quest.status === 'active' && quest.progress !== undefined && (
        <div className="mt-3">
          <div className="h-1.5 bg-blox-dark rounded-full overflow-hidden">
            <div 
              className="h-full bg-blox-accent transition-all duration-300"
              style={{ width: `${quest.progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Voice button component
function VoiceButton({ 
  isListening, 
  isSpeaking,
  onToggle,
}: { 
  isListening: boolean;
  isSpeaking: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className={`
        relative w-14 h-14 rounded-2xl flex items-center justify-center
        transition-all duration-300
        ${isListening 
          ? 'bg-blox-accent text-blox-dark animate-pulse' 
          : isSpeaking
          ? 'bg-blox-epic text-white'
          : 'bg-blox-surface text-slate-300 hover:bg-blox-card hover:text-white'}
      `}
    >
      {isListening ? (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
          <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
        </svg>
      ) : isSpeaking ? (
        <svg className="w-6 h-6 animate-bounce" fill="currentColor" viewBox="0 0 24 24">
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
        </svg>
      ) : (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
        </svg>
      )}
      
      {/* Active indicator */}
      {(isListening || isSpeaking) && (
        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-blox-accent animate-ping" />
      )}
    </button>
  );
}

// Hint popup component
function HintPopup({ 
  hint, 
  onClose,
  onRequestMore,
}: { 
  hint: HintResponse;
  onClose: () => void;
  onRequestMore?: () => void;
}) {
  const categoryIcons = {
    conceptual: '💡',
    procedural: '📋',
    example: '✏️',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="relative max-w-md w-full rounded-2xl bg-blox-card border border-blox-surface p-6 animate-pop-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white"
        >
          ✕
        </button>

        {/* Hint content */}
        <div className="flex items-start gap-3 mb-4">
          <span className="text-3xl">{categoryIcons[hint.category]}</span>
          <div>
            <h3 className="font-display text-lg text-white mb-2">Power-Up Hint!</h3>
            <p className="text-slate-300">{hint.hint}</p>
          </div>
        </div>

        {/* Follow-up */}
        {hint.followUp && (
          <div className="mt-4 p-3 rounded-xl bg-blox-surface/50 border border-blox-surface">
            <p className="text-sm text-slate-400">
              <span className="text-blox-accent">Pro tip:</span> {hint.followUp}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-xl bg-blox-accent text-blox-dark font-semibold hover:bg-blox-accent/90 transition-colors"
          >
            Got it! 💪
          </button>
          {onRequestMore && hint.difficulty !== 'direct' && (
            <button
              onClick={onRequestMore}
              className="px-4 py-2 rounded-xl border border-slate-600 text-slate-300 hover:bg-blox-surface transition-colors"
            >
              Still stuck
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Main QuestHub component
export default function QuestHub() {
  // Player state
  const [stats, setStats] = useState<PlayerStats>({
    level: 5,
    currentXP: 340,
    xpToNextLevel: 500,
    totalXP: 2340,
    streak: 3,
    coins: 125,
  });

  const [profile, _setProfile] = useState<StudentProfile>({
    userId: 'demo_user',
    isChild: true,
    currentLevel: 5,
    totalXP: 2340,
    streakDays: 3,
    masteredSkills: ['algebra.linear', 'calc.limits'],
    strugglingSkills: ['calc.power_rule'],
    recentAttempts: [],
  });

  // Scenario system state
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<MathLevel>('algebra_1');
  const [masteredLenses, setMasteredLenses] = useState<QuestionLens[]>([]);
  const [viewMode, setViewMode] = useState<'scenarios' | 'classic'>('scenarios');

  // Quests state (classic mode)
  const [quests, setQuests] = useState<Quest[]>([
    {
      id: 'q1',
      title: 'Power Rule Sprint',
      skillTag: 'calc.power_rule',
      difficulty: 'medium',
      xpReward: 30,
      status: 'available',
    },
    {
      id: 'q2',
      title: 'Polynomial Boss Battle',
      skillTag: 'calc.polynomials',
      difficulty: 'hard',
      xpReward: 50,
      status: 'locked',
    },
    {
      id: 'q3',
      title: 'Quick Derivatives',
      skillTag: 'calc.power_rule',
      difficulty: 'easy',
      xpReward: 20,
      status: 'completed',
    },
  ]);

  const [recommendations, setRecommendations] = useState<ChallengeRecommendation[]>([]);
  const [currentHint, setCurrentHint] = useState<HintResponse | null>(null);
  const [hintAttempt, setHintAttempt] = useState(1);

  // Voice hook
  const voice = useVoice({
    persona: 'coach',
    onTranscript: handleVoiceCommand,
    onError: (err) => console.error('Voice error:', err),
  });

  // Handle voice commands
  function handleVoiceCommand(text: string) {
    const lower = text.toLowerCase();
    
    tracker.trackUser({
      action: 'voice_command',
      metadata: { text },
      userId: profile.userId,
      isChild: profile.isChild,
    });

    // Simple command matching
    if (lower.includes('help') || lower.includes('hint') || lower.includes('stuck')) {
      requestHint();
    } else if (lower.includes('start') || lower.includes('quest') || lower.includes('begin')) {
      const availableQuest = quests.find(q => q.status === 'available');
      if (availableQuest) {
        handleStartQuest(availableQuest.id);
      }
    } else if (lower.includes('level') || lower.includes('progress') || lower.includes('status')) {
      voice.speak(`You're level ${stats.level} with ${stats.currentXP} XP. ${stats.xpToNextLevel - stats.currentXP} more to level up!`);
    }
  }

  // Load recommendations on mount
  useEffect(() => {
    loadRecommendations();
    
    // Welcome message
    setTimeout(() => {
      voice.speakLine('welcome');
    }, 500);
  }, []);

  async function loadRecommendations() {
    const availableChallenges = quests
      .filter(q => q.status === 'available' || q.status === 'locked')
      .map(q => ({
        templateId: q.id,
        title: q.title,
        skillTag: q.skillTag,
        difficulty: q.difficulty,
      }));

    const recs = await getRecommendations(profile, availableChallenges);
    setRecommendations(recs);
  }

  function handleStartQuest(questId: string) {
    setQuests(prev => prev.map(q => 
      q.id === questId ? { ...q, status: 'active', progress: 0 } : q
    ));

    voice.speakLine('questStart');

    tracker.trackChallenge({
      challengeId: questId,
      templateId: questId,
      skillTag: quests.find(q => q.id === questId)?.skillTag || '',
      action: 'start',
      userId: profile.userId,
      isChild: profile.isChild,
    });
  }

  async function requestHint() {
    const activeQuest = quests.find(q => q.status === 'active');
    if (!activeQuest) {
      voice.speak("No active quest right now. Start one to get hints!");
      return;
    }

    const hint = await getHint({
      question: `Differentiate: f(x) = 5x^3 - 2x + 7`, // Would come from actual quest
      skillTag: activeQuest.skillTag,
      attemptNumber: hintAttempt,
      profile,
    });

    setCurrentHint(hint);
    voice.speak(hint.hint);
  }

  function handleRequestMoreHint() {
    setHintAttempt(prev => prev + 1);
    setCurrentHint(null);
    requestHint();
  }

  function toggleVoice() {
    if (voice.isListening) {
      voice.stopListening();
    } else {
      voice.startListening();
    }
  }

  // Handle scenario quest completion
  function handleScenarioQuestComplete(
    lens: QuestionLens,
    result: { score: number; passed: boolean; xpEarned: number }
  ) {
    if (result.passed) {
      // Add XP
      setStats(prev => {
        const newXP = prev.currentXP + result.xpEarned;
        const levelUp = newXP >= prev.xpToNextLevel;
        
        if (levelUp) {
          voice.speakLine('levelUp');
        }
        
        return {
          ...prev,
          currentXP: levelUp ? newXP - prev.xpToNextLevel : newXP,
          totalXP: prev.totalXP + result.xpEarned,
          level: levelUp ? prev.level + 1 : prev.level,
          coins: prev.coins + Math.round(result.xpEarned / 5),
        };
      });
      
      // Mark lens as mastered
      if (result.score >= 0.8) {
        setMasteredLenses(prev => [...new Set([...prev, lens])]);
      }
    }
  }

  return (
    <div className="min-h-screen bg-blox-darker bg-grid-pattern bg-[size:50px_50px]">
      {/* Ambient glow */}
      <div className="fixed inset-0 bg-glow-radial pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <header className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl text-white tracking-wider">
              QUEST<span className="text-blox-accent">HUB</span>
            </h1>
            <p className="text-sm text-slate-400 mt-1">Level up your brain 🧠</p>
          </div>

          <div className="flex items-center gap-4">
            {/* Coins */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blox-surface border border-blox-gold/30">
              <span className="text-xl animate-coin-spin" style={{ animationDuration: '3s' }}>🪙</span>
              <span className="font-display text-blox-gold">{stats.coins}</span>
            </div>

            {/* Voice button */}
            <VoiceButton 
              isListening={voice.isListening}
              isSpeaking={voice.isSpeaking}
              onToggle={toggleVoice}
            />
          </div>
        </header>

        {/* Stats bar */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 mb-8">
          <XPBar 
            current={stats.currentXP} 
            max={stats.xpToNextLevel} 
            level={stats.level} 
          />
          <StreakFlame days={stats.streak} />
        </div>

        {/* Voice transcript */}
        {voice.isListening && voice.transcript && (
          <div className="mb-6 p-4 rounded-2xl bg-blox-accent/10 border border-blox-accent/30 animate-slide-up">
            <div className="flex items-center gap-2 text-blox-accent">
              <span className="animate-pulse">🎤</span>
              <span className="font-mono text-sm">{voice.transcript}</span>
            </div>
          </div>
        )}

        {/* View mode toggle */}
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => setViewMode('scenarios')}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              viewMode === 'scenarios'
                ? 'bg-blox-accent text-blox-dark'
                : 'bg-blox-surface text-slate-300 hover:bg-blox-card'
            }`}
          >
            🎯 Story Quests
          </button>
          <button
            onClick={() => setViewMode('classic')}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              viewMode === 'classic'
                ? 'bg-blox-accent text-blox-dark'
                : 'bg-blox-surface text-slate-300 hover:bg-blox-card'
            }`}
          >
            📜 Classic Quests
          </button>
        </div>

        {/* SCENARIO MODE */}
        {viewMode === 'scenarios' && (
          <>
            {/* Level selector */}
            <div className="mb-6 p-4 rounded-xl bg-blox-surface/50">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Math Level</p>
              <div className="flex flex-wrap gap-2">
                {LEVEL_ORDER.slice(0, 6).map(level => (
                  <button
                    key={level}
                    onClick={() => setSelectedLevel(level)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                      selectedLevel === level
                        ? 'bg-blox-accent text-blox-dark font-semibold'
                        : 'bg-blox-card text-slate-300 hover:bg-blox-surface'
                    }`}
                  >
                    {LEVEL_INFO[level].name}
                  </button>
                ))}
              </div>
            </div>

            {/* Scenario selection or active quest */}
            {!selectedScenario ? (
              <section>
                <h2 className="font-display text-xl text-white mb-4 flex items-center gap-2">
                  <span className="text-blox-accent">🎮</span> Choose Your Adventure
                </h2>
                <p className="text-sm text-slate-400 mb-4">
                  Pick a story. Master it from arithmetic to calculus. Same game, deeper math!
                </p>
                <ScenarioSelector 
                  onSelect={setSelectedScenario}
                  selectedId={selectedScenario || undefined}
                />
              </section>
            ) : (
              <section>
                <button
                  onClick={() => setSelectedScenario(null)}
                  className="mb-4 px-4 py-2 rounded-lg text-sm bg-blox-surface text-slate-300 hover:bg-blox-card transition-colors"
                >
                  ← Choose Different Story
                </button>
                
                <ScenarioQuestHub
                  scenarioId={selectedScenario}
                  level={selectedLevel}
                  childId={profile.userId}
                  masteredLenses={masteredLenses}
                  onQuestComplete={handleScenarioQuestComplete}
                />
              </section>
            )}
          </>
        )}

        {/* CLASSIC MODE */}
        {viewMode === 'classic' && (
          <>
        {/* Recommended quests */}
        {recommendations.length > 0 && (
          <section className="mb-8">
            <h2 className="font-display text-xl text-white mb-4 flex items-center gap-2">
              <span className="text-blox-accent">⚡</span> Recommended for You
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recommendations.map(rec => {
                const quest = quests.find(q => q.id === rec.templateId);
                if (!quest) return null;
                return (
                  <QuestCard
                    key={quest.id}
                    quest={quest}
                    recommended={rec}
                    onStart={() => handleStartQuest(quest.id)}
                  />
                );
              })}
            </div>
          </section>
        )}

        {/* All quests */}
        <section>
          <h2 className="font-display text-xl text-white mb-4 flex items-center gap-2">
            <span className="text-blox-gold">📜</span> All Quests
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {quests.map(quest => (
              <QuestCard
                key={quest.id}
                quest={quest}
                onStart={() => handleStartQuest(quest.id)}
                onContinue={quest.status === 'active' ? () => {} : undefined}
              />
            ))}
          </div>
        </section>

        {/* Active quest help button */}
        {viewMode === 'classic' && quests.some(q => q.status === 'active') && (
          <button
            onClick={requestHint}
            className="fixed bottom-6 right-6 px-6 py-3 rounded-2xl bg-blox-rare text-white font-semibold shadow-lg shadow-blox-rare/30 hover:bg-blox-rare/90 transition-all hover:scale-105"
          >
            💡 Need a hint?
          </button>
        )}

        {/* Hint popup */}
        {currentHint && (
          <HintPopup
            hint={currentHint}
            onClose={() => setCurrentHint(null)}
            onRequestMore={handleRequestMoreHint}
          />
        )}
          </>
        )}
      </div>
    </div>
  );
}
